import React, { FC, useState } from 'react';
import type { OrganisationMember } from '@dreamai/types';
import { ReactComponent as ShieldTickIcon } from '../../icons/shield-tick.svg';
import { ReactComponent as UsersCheckIcon } from '../../icons/users-check.svg';
import { ReactComponent as AlertTriangleIcon } from '../../icons/alert-triangle-outline.svg';
import { ReactComponent as CloseIcon } from '../../icons/x-close.svg';
import OrganisationModal from './OrganisationModal';
import { INSTRUCTION_MAX_LENGTH, memberLabel } from './informationBarriers';

export interface BarrierFormValues {
  instruction: string;
  /** Memberships exempted from the barrier; empty = everyone is covered. */
  exemptIds: string[];
}

/** Advisory verdict from the quality check; the next save goes through anyway. */
export interface WeakRuleWarning {
  text: string;
  missing: string[];
}

interface InformationBarrierModalProps {
  mode: 'create' | 'edit';
  values: BarrierFormValues;
  /** People who can be exempted */
  members: OrganisationMember[];
  teamAssistantsAvailable: boolean;
  error: string | null;
  /** The instruction was refused as empty */
  invalidInstruction: boolean;
  warning: WeakRuleWarning | null;
  checking: boolean;
  saving: boolean;
  onChange: (values: BarrierFormValues) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const MATCH_LIMIT = 6;
/** Show the character count once the text gets close to the backend's limit. */
const COUNTER_FROM = INSTRUCTION_MAX_LENGTH - 200;

const normalise = (value: string) =>
  value.trim().replace(/\s+/g, ' ').toLowerCase();

/** "Jol" in "Jolene Brown" → <strong>Jol</strong>ene Brown */
function highlight(name: string, needle: string) {
  const at = name.toLowerCase().indexOf(needle);
  if (at < 0 || !needle) return name;
  return (
    <>
      {name.slice(0, at)}
      <strong>{name.slice(at, at + needle.length)}</strong>
      {name.slice(at + needle.length)}
    </>
  );
}

export function ScopeSummary({
  exempt,
  teamAssistantsAvailable,
  onRemove,
}: {
  exempt: OrganisationMember[];
  teamAssistantsAvailable: boolean;
  onRemove?: (member: OrganisationMember) => void;
}) {
  return (
    <div className='organisation-barrier-scope'>
      <UsersCheckIcon aria-hidden='true' />
      <div className='organisation-barrier-scope-body'>
        <p className='organisation-barrier-scope-title'>
          {exempt.length === 0
            ? 'This rule applies to everyone in your organisation'
            : 'This rule applies to everyone in your organisation, except:'}
        </p>
        {exempt.length > 0 && (
          <ul className='organisation-barrier-tags'>
            {exempt.map((m) => (
              <li key={m.id} className='organisation-barrier-tag'>
                {memberLabel(m)}
                {onRemove && (
                  <button
                    type='button'
                    aria-label={`Remove ${memberLabel(m)}`}
                    onClick={() => onRemove(m)}
                  >
                    <CloseIcon aria-hidden='true' />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {exempt.length > 0 && teamAssistantsAvailable && (
          <p className='organisation-barrier-scope-note'>
            The Team Secretary still follows this rule for everyone.
          </p>
        )}
      </div>
    </div>
  );
}

/** Create / edit dialog for one information barrier (Figma "Add new barrier"). */
const InformationBarrierModal: FC<InformationBarrierModalProps> = ({
  mode,
  values,
  members,
  teamAssistantsAvailable,
  error,
  invalidInstruction,
  warning,
  checking,
  saving,
  onChange,
  onSubmit,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const busy = checking || saving;
  const set = (patch: Partial<BarrierFormValues>) =>
    onChange({ ...values, ...patch });

  const byId = new Map(members.map((m) => [m.id, m]));
  const exempt = values.exemptIds
    .map((id) => byId.get(id))
    .filter((m): m is OrganisationMember => !!m);
  const candidates = members.filter((m) => !values.exemptIds.includes(m.id));
  const needle = normalise(query);
  const matches = needle
    ? candidates
        .filter((m) => normalise(memberLabel(m)).includes(needle))
        .slice(0, MATCH_LIMIT)
    : [];
  const exactMatch = needle
    ? candidates.find((m) => normalise(m.name || '') === needle)
    : undefined;

  const addExemption = (member: OrganisationMember) => {
    set({ exemptIds: [...values.exemptIds, member.id] });
    setQuery('');
  };
  const removeExemption = (member: OrganisationMember) =>
    set({ exemptIds: values.exemptIds.filter((id) => id !== member.id) });

  const editing = mode === 'edit';
  const label = editing ? 'Save changes' : 'Add new barrier';
  const busyLabel = checking ? 'Checking…' : 'Saving…';
  const length = values.instruction.length;

  return (
    <OrganisationModal
      icon={<ShieldTickIcon />}
      title={editing ? 'Edit barrier' : 'Add new barrier'}
      error={error}
      busy={busy}
      testId='information-barrier-modal'
      onClose={onClose}
      onSubmit={() => {
        if (!busy) onSubmit();
      }}
      actions={
        <>
          <button
            type='button'
            className='organisation-modal-cancel'
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='organisation-modal-primary organisation-barrier-submit'
            disabled={busy}
            aria-label={busy ? busyLabel : undefined}
            aria-busy={busy}
          >
            {busy && (
              <span
                className='organisation-barrier-spinner'
                aria-hidden='true'
              />
            )}
            {label}
          </button>
        </>
      }
    >
      <div className='organisation-modal-panel organisation-barrier-panel'>
        <div className='organisation-modal-field'>
          <label
            className='organisation-modal-label'
            htmlFor='information-barrier-instruction'
          >
            What should a secretary not read, discuss, or bring up?
          </label>
          <p
            className='organisation-modal-hint'
            id='information-barrier-instruction-hint'
          >
            Only admins see this. Be specific, this means it’s applied more
            accurately.
          </p>
          <textarea
            id='information-barrier-instruction'
            className={`organisation-modal-input organisation-barrier-textarea${warning ? ' weak' : ''}`}
            value={values.instruction}
            maxLength={INSTRUCTION_MAX_LENGTH}
            autoFocus
            disabled={busy}
            aria-invalid={invalidInstruction}
            aria-describedby='information-barrier-instruction-hint'
            onChange={(e) => set({ instruction: e.target.value })}
          />
          {length >= COUNTER_FROM && (
            <p className='organisation-modal-hint organisation-barrier-count'>
              {length.toLocaleString('en-GB')} /{' '}
              {INSTRUCTION_MAX_LENGTH.toLocaleString('en-GB')}
            </p>
          )}
          {warning && (
            <div className='organisation-barrier-warning' role='status'>
              <AlertTriangleIcon aria-hidden='true' />
              <div>
                <p className='organisation-barrier-warning-text'>
                  {warning.text}
                </p>
                <p className='organisation-barrier-warning-detail'>
                  {warning.missing.length > 0 &&
                    `Add: ${warning.missing.join(', ')}. `}
                  You can still save this, but it may not work as expected.
                </p>
              </div>
            </div>
          )}
        </div>

        <ScopeSummary
          exempt={exempt}
          teamAssistantsAvailable={teamAssistantsAvailable}
          onRemove={busy ? undefined : removeExemption}
        />

        <div className='organisation-modal-field'>
          <label
            className='organisation-modal-label'
            htmlFor='information-barrier-exempt'
          >
            Add members this doesn’t apply to
          </label>
          <p
            className='organisation-modal-hint'
            id='information-barrier-exempt-hint'
          >
            Type to search. People in this list will NOT have this barrier
            applied.
          </p>
          <div className='organisation-barrier-search'>
            <div className='organisation-barrier-search-row'>
              <input
                id='information-barrier-exempt'
                type='text'
                className='organisation-modal-input'
                placeholder='Search for a user'
                autoComplete='off'
                value={query}
                disabled={busy}
                aria-describedby='information-barrier-exempt-hint'
                aria-expanded={!!needle}
                aria-controls='information-barrier-matches'
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (exactMatch) addExemption(exactMatch);
                }}
              />
              <button
                type='button'
                className='organisation-barrier-add'
                disabled={busy || !exactMatch}
                onClick={() => exactMatch && addExemption(exactMatch)}
              >
                Add
              </button>
            </div>
            {needle && (
              <ul
                id='information-barrier-matches'
                className='organisation-barrier-matches'
              >
                {matches.map((m) => (
                  <li key={m.id}>
                    <button
                      type='button'
                      aria-label={memberLabel(m)}
                      disabled={busy}
                      onClick={() => addExemption(m)}
                    >
                      {highlight(memberLabel(m), needle)}
                    </button>
                  </li>
                ))}
                {matches.length === 0 && (
                  <li className='organisation-barrier-no-match'>
                    Nobody matching “{query.trim()}” in your organisation
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    </OrganisationModal>
  );
};

export default InformationBarrierModal;
